import { useState } from 'react'

import { cap } from '../../../State'
import { translations } from '../../../translations'
import { useComando } from './api'
import DialogConferma from './DialogConferma'
import {
  erroriGoelett,
  formGoelett,
  goelettRilevante,
  payloadConnessioneGoelett,
  payloadRevocaGoelett,
  statoConnessione,
  testoDestinazione
} from './Functions'
import Select from './Select'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Campo di testo tipizzato del form Goelett.
 * @param {Object} props - Proprietà
 * @returns {import('react').JSX.Element}
 */
const Campo = ({ id, label, value, onChange, hint, errore, type = 'text' }) => (
  <div className="nfield" style={{ gridColumn: 'span 6' }}>
    <label htmlFor={id}>{label}</label>
    <input
      id={id}
      type={type}
      className="p-inputtext p-component"
      value={value}
      autoComplete="off"
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={!!errore}
      style={{ width: '100%' }}
    />
    {errore ? (
      <span className="nfield__hint" style={{ color: 'var(--danger)' }}>
        {errore}
      </span>
    ) : (
      hint && <span className="nfield__hint">{hint}</span>
    )}
  </div>
)

/**
 * «Collegamento Goelett» (09 §9.1.6, §8.4): stato della connessione, form tipizzato
 * (mai JSON) precompilato col salvato — del segreto solo il nome del riferimento — e
 * revoca. Una connessione revocata si riattiva salvandola di nuovo.
 *
 * Il form è aperto quando serve: una configurazione salvata usa Prenotazione Goelett o il
 * collegamento esiste già. Altrimenti la card resta una riga che dice a cosa serve, con
 * «Configura il collegamento»: la card non sparisce, perché il server non ammette
 * Prenotazione Goelett finché il collegamento non è salvato.
 * @param {Object} props - Proprietà
 * @param {Object} props.overview - DTO overview
 * @param {boolean} [props.aperto] - Form aperto a mano (conta solo se non è rilevante)
 * @param {(aperto: boolean) => void} [props.onCambiaAperto] - Apre o chiude il form
 * @returns {import('react').JSX.Element}
 */
export const CollegamentoGoelett = ({ overview, aperto = false, onCambiaAperto }) => {
  const stato = overview.providerStatus?.goelett
  const [form, setForm] = useState(() => formGoelett(stato))
  const [revoca, setRevoca] = useState(false)
  const { esegui, inCorso } = useComando()
  const errori = erroriGoelett(form)
  const { label: labelStato, badge } = statoConnessione(stato, t)
  const attiva = stato?.configured && stato.status === 'active'
  const rilevante = goelettRilevante(overview)
  const formVisibile = rilevante || aperto

  const imposta = (campo) => (valore) => setForm((old) => ({ ...old, [campo]: valore }))
  const erroreVisibile = (campo, testo) =>
    (form[campo] || '').trim() !== '' && errori.includes(campo) ? testo : undefined

  const salva = async () => {
    const esito = await esegui(
      payloadConnessioneGoelett({
        environmentId: overview.environment.id,
        stato,
        form
      }),
      { titolo: t.cnGoelettSalvato, dettaglio: t.cnSoloNuoveChat }
    )
  }

  const revocaConnessione = async () => {
    const esito = await esegui(
      payloadRevocaGoelett({ environmentId: overview.environment.id, stato })
    )
    if (esito !== 'errore') setRevoca(false)
  }

  return (
    <section
      className="ncard"
      id="cn-goelett"
      aria-labelledby="cn-titolo-goelett"
      data-testid="cn-goelett"
      style={{ scrollMarginTop: 16 }}
    >
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-link" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title" id="cn-titolo-goelett">
            {t.cnGoelettTitolo}
          </h3>
          <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>{t.cnGoelettHint}</span>
        </div>
        {!rilevante && (
          <div className="ncard__head-actions">
            {!aperto && <span className="nbadge">{t.cnConnessioneNonUsata}</span>}
            <button
              type="button"
              className="nbtn nbtn--ghost nbtn--sm"
              aria-expanded={aperto}
              aria-controls="cn-goelett-corpo"
              onClick={() => onCambiaAperto?.(!aperto)}
            >
              {aperto ? t.cnNascondi : t.cnConfiguraGoelett}
            </button>
          </div>
        )}
      </div>
      {formVisibile && (
        <div id="cn-goelett-corpo" className="ncard__body" style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13 }}>{t.cnGoelettStato}:</span>
            <span className={`nbadge ${badge}`}>{labelStato}</span>
            {stato?.configured && (
              <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                {testoDestinazione(stato.destination, t)}
              </span>
            )}
            {stato?.reason && (
              <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>{stato.reason}</span>
            )}
            {attiva && (
              <button
                type="button"
                className="nbtn nbtn--sm nbtn--danger"
                onClick={() => setRevoca(true)}
                disabled={inCorso}
              >
                {t.cnRevocaConnessione}
              </button>
            )}
          </div>
          <div className="nrow">
            <div className="nfield" style={{ gridColumn: 'span 6' }}>
              <label htmlFor="cn-goelett-label">{t.cnGoelettAmbiente}</label>
              <Select id="cn-goelett-label" value={form.label} onChange={imposta('label')}>
                <option value="sandbox">{t.cnDestinazioneSandbox}</option>
                <option value="production">{t.cnDestinazioneProduzione}</option>
              </Select>
            </div>
            <Campo
              id="cn-goelett-baseUrl"
              label={t.cnGoelettBaseUrl}
              value={form.baseUrl}
              onChange={imposta('baseUrl')}
              hint={t.cnGoelettBaseUrlHint}
              errore={erroreVisibile('baseUrl', t.cnGoelettUrlNonValido)}
            />
            <Campo
              id="cn-goelett-stsUrl"
              label={t.cnGoelettStsUrl}
              value={form.stsUrl}
              onChange={imposta('stsUrl')}
              errore={erroreVisibile('stsUrl', t.cnGoelettStsNonValido)}
            />
            <Campo
              id="cn-goelett-oauthScope"
              label={t.cnGoelettScope}
              value={form.oauthScope}
              onChange={imposta('oauthScope')}
            />
            <Campo
              id="cn-goelett-clientId"
              label={t.cnGoelettClientId}
              value={form.clientId}
              onChange={imposta('clientId')}
            />
            <Campo
              id="cn-goelett-clientSecretRef"
              label={t.cnGoelettSecretRef}
              value={form.clientSecretRef}
              onChange={imposta('clientSecretRef')}
              hint={t.cnGoelettSecretRefHint}
            />
          </div>
          <div>
            <button
              type="button"
              className="nbtn nbtn--primary"
              onClick={salva}
              disabled={inCorso || errori.length > 0}
            >
              {t.cnGoelettSalva}
            </button>
          </div>
        </div>
      )}

      <DialogConferma
        visible={revoca}
        onHide={() => setRevoca(false)}
        titolo={t.cnRevocaConnessione}
        descrizione={t.cnDescRevocaConnessione}
        avviso={t.cnAvvisoEffettiInCorso}
        etichettaConferma={t.cnRevocaConnessione}
        inCorso={inCorso}
        onConferma={revocaConnessione}
      />
    </section>
  )
}

export default CollegamentoGoelett
